import {type SubmitHandler, useForm} from "react-hook-form";
import {useNavigate} from "react-router-dom";
import {showErrorToast, showSuccessToast} from "@/utils/toast";
import {Field, Fieldset} from "@headlessui/react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {Button, Label, PasswordInput, PhoneInput} from "@chayka/ui";
import {trpc} from "@/app/trpc";

interface Inputs {
  phone: string;
  password: string;
}

export const SignInPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: {isValid},
  } = useForm<Inputs>();

  const signInMutator = useMutation({
    ...trpc.auth.signIn.mutationOptions(),
    onSuccess: async () => {
      try {
        await queryClient.fetchQuery({
          ...trpc.auth.me.queryOptions(),
          staleTime: 0,
        });
        showSuccessToast("Вы успешно вошли в аккаунт");
        navigate("/app", {replace: true});
      } catch {
        showErrorToast(
          "Не удалось подтвердить сессию. Проверьте настройки приложения и попробуйте снова.",
        );
      }
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const onSubmit: SubmitHandler<Inputs> = (data) =>
    signInMutator.mutate({
      ...data,
      phone: data.phone.replace(/\D/g, ""),
    });

  return (
    <>
      <Fieldset className="w-full flex flex-col gap-5">
        <Field>
          <Label>Телефон</Label>
          <PhoneInput clearable {...register("phone", {required: true})} />
        </Field>
        <Field>
          <Label>Пароль</Label>
          <PasswordInput
            type="password"
            autoComplete="current-password"
            placeholder="Введите пароль"
            {...register("password", {required: true})}
          />
        </Field>
      </Fieldset>
      <Button
        className="self-stretch"
        size="medium"
        onClick={handleSubmit(onSubmit)}
        disabled={!isValid}
        loading={signInMutator.isPending}
      >
        Войти
      </Button>

      <a
        href="https://chaykastudia.ru/wp-login.php?action=lostpassword"
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-primary"
      >
        Забыли пароль?
      </a>

      <div className="text-sm">
        Ещё нет аккаунта?{" "}
        <span className="text-primary" onClick={() => navigate("/auth/sign-up")}>
          Зарегистрироваться
        </span>
      </div>
    </>
  );
};
